import { apiFetch } from "../utils/api";
import { Customer } from "./customers";
import { CustomerVehicle } from "./customerVehicles";
import { InquiryChecklist } from "./inquiryChecklists";
import { UserProfile } from "./auth";
import type { ProductVariant } from "./productVariants";

export type OrderStatus =
  | "initiated"
  | "draft"
  | "approved"
  | "confirmed"
  | "in_progress"
  | "completed"
  | "verified"
  | "done"
  | "delivered"
  | "cancelled";

export interface OrderItem {
  id: string;
  orderId: string;
  productVariantId: string | null;
  productVariant?: ProductVariant | null;
  description: string | null;
  itemType: string | null;
  quantity: string;
  unitPrice: string | null;
  lineSubtotal: string | null;
  discountTotal: string | null;
  taxTotal: string | null;
  lineTotal: string | null;
  cartWideDiscountId?: string | null;
  itemWiseDiscountId?: string | null;
  metadata: Record<string, unknown> | null;
  technicianId: string | null;
  nextDueAfterDays?: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  serviceTechnicians?: OrderServiceTechnician[];
}

export interface OrderServiceTechnician {
  id: string;
  orderId: string;
  orderItemId: string;
  technicianId: string;
  technician?: UserProfile | null;
  bayId?: string | null;
  bay?: { id: string; name: string } | null;
  orderItem?: OrderItem | null;
  completed: boolean;

  notes: string | null;
  completedAt: string | null;
  startTime?: string | null;
  endTime?: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface OrderServiceAssignmentInput {
  orderItemId: string;
  technicianId: string | null;
  bayId?: string | null;
  completed?: boolean;
  notes?: string | null;
  completedAt?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  nextDueAfterDays?: number | null;
}

export interface OrderChecklistEntry {
  id: string;
  orderId: string;
  checklistId: string;
  type: "inspection" | "pre_delivery";
  response: unknown;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrderResponse {
  id: string;
  erpId: number | null;
  saleEstimateErpId?: number | null;
  orderNumber: string;
  customerId: string;
  customer?: Customer;
  customerVehicleId: string | null;
  customerVehicle?: CustomerVehicle | null;
  customerNotes: string | null;
  customerApproved: boolean;
  customerApprovedAt: string | null;
  customerApprovedById: string | null;
  customerApprovedBy?: UserProfile | null;
  managerApprovalNotes: string | null;
  technicianNotes: string | null;
  cancellationNotes?: string | null;
  managerApproved: boolean;
  managerApprovedAt: string | null;
  managerApprovedById: string | null;
  managerApprovedBy?: UserProfile | null;
  technicianCompletedTheJobAt: string | null;
  cancelledAt?: string | null;
  status: OrderStatus;
  metadata: Record<string, unknown> | null;
  subtotalAmount: string | null;
  discountTotal: string | null;
  taxTotal: string | null;
  grandTotal: string | null;
  counterCode: string | null;
  locationCode: string | null;
  locationId?: string | null;
  createdById: string | null;
  createdBy?: UserProfile | null;
  managerId: string | null;
  manager?: UserProfile | null;
  createdAt: string;
  updatedAt: string;
  appointment?: any;
  items: OrderItem[];
  checklists: OrderChecklistEntry[];
  serviceTechnicians?: OrderServiceTechnician[];
  technicians?: UserProfile[];

  paymentSummaries?: Array<{
    id: string;
    orderId: string;
    paymentTypeErpId: number;
    paymentTypeName: string;
    amount: string | null;
    happenedAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  aiAnalysis?: Record<string, unknown> | null;
}

export interface CreateOrderPayload {
  customerId: string;
  customerVehicleId: string;
  createdById: string;
  status?: OrderStatus;
  locationId?: string | null;
  customerNotes?: string | null;
  customerApproved?: boolean;
  customerApprovedAt?: string | null;
  customerApprovedById?: string | null;
  managerApprovalNotes?: string | null;
  managerApproved?: boolean;
  managerApprovedAt?: string | null;
  managerApprovedById?: string | null;
  technicianCompletedTheJobAt?: string | null;
  technicianNotes?: string | null;
  managerId?: string | null;
  technicianIds?: string[];
  items?: unknown[];
  checklists?: unknown[];
  serviceAssignments?: OrderServiceAssignmentInput[];
}

export interface PaginatedOrdersResponse {
  data: OrderResponse[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface FetchOrdersParams {
  page?: number;
  limit?: number;
  status?: OrderStatus;
  customerId?: string;
  vehicleBrandId?: string;
  vehicleModelId?: string;
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  locationErpIds?: number[];
}

export const createOrder = (
  payload: CreateOrderPayload
): Promise<OrderResponse> =>
  apiFetch<OrderResponse>("/orders", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const fetchOrder = (orderId: string): Promise<OrderResponse> =>
  apiFetch<OrderResponse>(`/orders/${orderId}`);

export interface OrderInspectionChecklistsResponse {
  general: InquiryChecklist[];
  specific: InquiryChecklist[];
}

export const fetchOrderInspectionChecklists = (
  orderId: string
): Promise<OrderInspectionChecklistsResponse> =>
  apiFetch<OrderInspectionChecklistsResponse>(
    `/orders/${orderId}/inspection-checklists`
  );

export const fetchOrderPreDeliveryChecklists = (
  orderId: string
): Promise<OrderInspectionChecklistsResponse> =>
  apiFetch<OrderInspectionChecklistsResponse>(
    `/orders/${orderId}/pre-delivery-checklists`
  );

export interface UpdateOrderPayload {
  customerId?: string;
  customerVehicleId?: string;
  status?: OrderStatus;
  metadata?: Record<string, unknown> | null;
  customerNotes?: string | null;
  customerApproved?: boolean;
  customerApprovedAt?: string | null;
  customerApprovedById?: string | null;
  managerApprovalNotes?: string | null;
  managerApproved?: boolean;
  managerApprovedAt?: string | null;
  managerApprovedById?: string | null;
  technicianCompletedTheJobAt?: string | null;
  technicianNotes?: string | null;
  cancellationNotes?: string | null;
  cancelledAt?: string | null;
  managerId?: string | null;
  subtotalAmount?: number | string | null;
  discountTotal?: number | string | null;
  taxTotal?: number | string | null;
  grandTotal?: number | string | null;
  technicianIds?: string[];
  items?: unknown[];
  checklists?: Array<{
    id?: string;
    checklistId: string;
    type?: string;
    response?: unknown;
    sortOrder?: number | null;
  }>;
  serviceAssignments?: OrderServiceAssignmentInput[];
}

export const updateOrder = (
  orderId: string,
  payload: UpdateOrderPayload
): Promise<OrderResponse> =>
  apiFetch<OrderResponse>(`/orders/${orderId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export interface AddCartItemPayload {
  productVariantId?: string;
  description?: string | null;
  itemType?: string | null;
  quantity: number;
  unitPrice?: number | null;
  metadata?: Record<string, unknown> | null;
  technicianId?: string | null;
}

export interface UpdateCartItemPayload {
  orderItemId: string;
  quantity?: number;
  unitPrice?: number | null;
  metadata?: Record<string, unknown> | null;
  technicianId?: string | null;
}

export const addItemToCart = (orderId: string, payload: AddCartItemPayload) =>
  apiFetch<OrderResponse>(`/orders/${orderId}/cart/items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateItemInCart = (orderId: string, itemId: string, payload: UpdateCartItemPayload) =>
  apiFetch<OrderResponse>(`/orders/${orderId}/cart/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const fetchOrders = (
  params: FetchOrdersParams = {}
): Promise<PaginatedOrdersResponse> => {
  const searchParams = new URLSearchParams();

  if (params.page) {
    searchParams.set("page", String(params.page));
  }

  if (params.limit) {
    searchParams.set("limit", String(params.limit));
  }

  if (params.status) {
    searchParams.set("status", params.status);
  }

  if (params.customerId) {
    searchParams.set("customerId", params.customerId);
  }

  if (params.vehicleBrandId) {
    searchParams.set("vehicleBrandId", params.vehicleBrandId);
  }

  if (params.vehicleModelId) {
    searchParams.set("vehicleModelId", params.vehicleModelId);
  }

  if (params.createdFrom) {
    searchParams.set("createdFrom", params.createdFrom);
  }

  if (params.createdTo) {
    searchParams.set("createdTo", params.createdTo);
  }

  if (params.search) {
    searchParams.set("search", params.search.trim());
  }

  if (params.locationErpIds && params.locationErpIds.length) {
    searchParams.set('locationErpIds', params.locationErpIds.join(','));
  }

  const query = searchParams.toString();
  return apiFetch<PaginatedOrdersResponse>(
    `/orders${query ? `?${query}` : ""}`
  );
};

export const createSaleEstimate = (orderId: string) =>
  apiFetch<unknown>(`/orders/${orderId}/sale-estimate`, {
    method: "POST",
  });

export interface RemoveCartItemPayload {
  orderItemId: string;
  reasons?: string | null;
  reminderAfterDays?: number | null;
}

export const removeOrderCartItem = (
  orderId: string,
  payload: RemoveCartItemPayload
) => apiFetch<OrderResponse>(`/orders/${orderId}/remove-item`, {
  method: "POST",
  body: JSON.stringify(payload),
});

// next-due is updated via serviceAssignments payload in updateOrder

// Request customer approval: generates OTP (30m) and returns expiry + URL (no email sent yet)
export const requestCustomerApproval = (orderId: string) =>
  apiFetch<{ ok: boolean; expiresAt?: string; approvalUrl?: string; error?: string }>(
    `/orders/${orderId}/request-customer-approval`,
    { method: 'POST' },
  );

// Fetch latest customer approval request metadata
export const fetchLatestCustomerApprovalRequest = (orderId: string) =>
  apiFetch<{ ok: boolean; data: { createdAt: string; expiresAt: string; email: string } | null }>(
    `/orders/${orderId}/customer-approval-request`,
  );

// Public endpoints for customer approval UI
export const fetchPublicOrder = (orderId: string) =>
  apiFetch<OrderResponse>(`/orders-public/${orderId}`);

export const setCustomerApproval = (
  orderId: string,
  payload: { approve: boolean; customerNotes?: string | null; token: string }
) =>
  apiFetch<OrderResponse>(`/orders-public/${orderId}/customer-approval`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const removePublicOrderItem = (
  orderId: string,
  payload: { itemId: string; token: string; reasons?: string | null; reminderAfterDays?: number | null }
) =>
  apiFetch<OrderResponse>(`/orders-public/${orderId}/remove-item`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// Public company config (currency symbol etc.)
export const fetchPublicCompanyConfig = () =>
  apiFetch<{ ok: boolean; data: { companyName: string | null; companyCode: string | null; currencySymbol: string | null } | null }>(
    `/company-config/public`,
  );

// Customer token via email + OTP (1 day validity)
export const createCustomerToken = (input: { email: string; orderId: string; otp: string }) =>
  apiFetch<{ ok: boolean; token?: string }>(`/customer-auth/token`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

// Customer "me" using provided token (do not use staff auth header)
export const fetchCustomerMe = (token: string) =>
  apiFetch<{ ok: boolean; user?: { role: 'CUSTOMER'; email: string; orderId: string } }>(`/customer-auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
